import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'Shared/utils/hooks/use-translation';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { getUserById } from 'Shared/selectors/workspace';
import { readNotification } from 'Shared/actions/notifications';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { fetchSingleFeedback, viewFeedback } from 'Shared/actions/Feedbacks.actions';
import { getFeedbackByUuid } from 'Shared/selectors/Feedbacks.selectors';
import Confetti from 'Shared/components/Confetti/Confetti';
import { WpModal, WpModalBody, WpModalFooter, WpModalHeader } from 'Wpds/components/modal';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

import FeedbackRecognitionContent from './FeedbackRecognitionContent/FeedbackRecognitionContent';

import './KudosPreviewModal.scss';

type Props = {
  isOpen: boolean;
  setIsOpen: Function;
  notification: {
    id: number;
    data: {
      feedback_uuid: number;
      link: string;
    };
  };
};

export default function KudosPreviewModal({ isOpen, setIsOpen, notification }: Props) {
  const dispatch = useDispatch();
  const feedback = useDeepEqualSelector(getFeedbackByUuid(notification.data?.feedback_uuid));
  const history = useHistory();
  const { user: currentUser } = useCurrentUser();
  const { tr } = useTranslation();
  const creatorFirstName = useSelector(getUserById(feedback?.creatorId))?.firstName;
  const { id: currentUserId, slug: currentUserSlug } = currentUser;
  const [cleared, setCleared] = useState(false);

  const handleIsOpenToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  useEffect(() => {
    dispatch(readNotification(notification.id));
  }, [dispatch, notification.id]);

  const handleViewKudosButtonClick = useCallback(() => {
    if (cleared) dispatch(viewFeedback(feedback?.uuid));
    history.push({
      pathname: PageUrlsUtils.makeUserProfilePageUrl(currentUserSlug),
      search: `?show=kudo&uuid=${feedback?.uuid}`,
    });
    handleIsOpenToggle();
  }, [cleared, dispatch, feedback, history, handleIsOpenToggle]);

  const handleClear = useCallback(() => setCleared(true), []);

  useEffect(() => {
    async function fetchFeedback() {
      await dispatch(fetchSingleFeedback(notification.data?.feedback_uuid));
    }
    fetchFeedback();
  }, [dispatch, notification.data, notification.id]);

  const viewed_by_recipient = feedback?.viewerIds.includes(currentUserId);

  if (!feedback) return null;

  if (viewed_by_recipient) return null;

  return (
    <>
      <Confetti darkBackdrop className="KudosPreviewModal__confetti" />
      <WpModal size="sm" hideBackdrop isOpen={isOpen} onToggle={handleIsOpenToggle}>
        <WpModalHeader>{tr('perform.kudos.preview_modal.title_gave_kudos', creatorFirstName)}</WpModalHeader>
        <WpModalBody>
          <FeedbackRecognitionContent onClear={handleClear} creatorFirstName={creatorFirstName} feedback={feedback} />
        </WpModalBody>
        <WpModalFooter>
          <WpButtonGroup>
            <WpButton variant="tertiary" onClick={handleIsOpenToggle}>
              {tr('perform.common.dismiss')}
            </WpButton>
            <WpButton qaTest="view-kudos" onClick={handleViewKudosButtonClick}>
              {tr('perform.kudos.preview_modal.view_kudos')}
            </WpButton>
          </WpButtonGroup>
        </WpModalFooter>
      </WpModal>
    </>
  );
}
